import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { rsvpFormSchema } from '@shared/schemas';
import type { RSVPFormData } from '@shared/types';
import Input from '../common/Input';
import Button from '../common/Button';
import GuestCounter from './GuestCounter';
import { useRSVP } from '../../hooks/useRSVP';
import { useState, useEffect } from 'react';

export default function RSVPForm() {
  const { submit, isLoading } = useRSVP();
  const [submitStatus, setSubmitStatus] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<RSVPFormData>({
    resolver: zodResolver(rsvpFormSchema),
    defaultValues: {
      name: '',
      email: '',
      attending: true,
      guestCount: 1,
      dietaryRestrictions: '',
    },
  });

  const attending = watch('attending');

  // Set guest count to 0 when not attending
  useEffect(() => {
    if (!attending) {
      setValue('guestCount', 0);
      setValue('dietaryRestrictions', '');
    } else if (attending && watch('guestCount') === 0) {
      setValue('guestCount', 1);
    }
  }, [attending, setValue, watch]);

  const onSubmit = async (data: RSVPFormData) => {
    try {
      const result = await submit(data);

      if (result.success) {
        setSubmitStatus({
          success: true,
          message: result.message,
        });
        reset();
      } else {
        setSubmitStatus({
          success: false,
          message: result.message,
        });
      }
    } catch (error) {
      setSubmitStatus({
        success: false,
        message: 'Failed to submit RSVP. Please try again.',
      });
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {submitStatus && (
        <div
          className={`mb-6 p-4 rounded-lg ${
            submitStatus.success
              ? 'bg-mint-light border-2 border-mint text-gray-800'
              : 'bg-red-50 border-2 border-red-300 text-red-800'
          }`}
        >
          <div className="flex items-start">
            <span className="text-2xl mr-3">
              {submitStatus.success ? '✅' : '❌'}
            </span>
            <div>
              <p className="font-semibold mb-1">
                {submitStatus.success ? 'Success!' : 'Error'}
              </p>
              <p>{submitStatus.message}</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Attendance Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Will you be attending? *
          </label>
          <Controller
            name="attending"
            control={control}
            render={({ field }) => (
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => field.onChange(true)}
                  className={`py-4 px-6 rounded-lg border-2 font-semibold transition-all duration-200 ${
                    field.value === true
                      ? 'bg-mint border-mint text-gray-800 shadow-md'
                      : 'bg-white border-gray-300 text-gray-600 hover:border-mint hover:bg-mint-light'
                  }`}
                  disabled={isLoading}
                >
                  ✅ Yes, I'll be there!
                </button>
                <button
                  type="button"
                  onClick={() => field.onChange(false)}
                  className={`py-4 px-6 rounded-lg border-2 font-semibold transition-all duration-200 ${
                    field.value === false
                      ? 'bg-gray-100 border-gray-400 text-gray-800 shadow-md'
                      : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                  disabled={isLoading}
                >
                  😔 Sorry, can't make it
                </button>
              </div>
            )}
          />
          {errors.attending && (
            <p className="mt-2 text-sm text-red-600">{errors.attending.message}</p>
          )}
        </div>

        {/* Name Input */}
        <Input
          {...register('name')}
          label="Full Name(s)"
          placeholder="John and Jane Doe"
          error={errors.name?.message}
          disabled={isLoading}
        />

        {/* Email Input */}
        <Input
          {...register('email')}
          type="email"
          label="Email Address"
          placeholder="john.doe@example.com"
          error={errors.email?.message}
          disabled={isLoading}
        />

        {/* Conditional fields - only show when attending */}
        {attending && (
          <>
            {/* Guest Count */}
            <Controller
              name="guestCount"
              control={control}
              render={({ field }) => (
                <GuestCounter
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.guestCount?.message}
                />
              )}
            />

            {/* Dietary Restrictions */}
            <div className="mb-4">
              <label
                htmlFor="dietaryRestrictions"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Dietary Restrictions (Optional)
              </label>
              <textarea
                {...register('dietaryRestrictions')}
                id="dietaryRestrictions"
                rows={4}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-pink focus:ring-4 focus:ring-pink-light transition-all duration-200 resize-none"
                placeholder="Let us know about any allergies or dietary requirements..."
                disabled={isLoading}
              />
              {errors.dietaryRestrictions && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.dietaryRestrictions.message}
                </p>
              )}
            </div>
          </>
        )}

        {/* Message for non-attending guests */}
        {!attending && (
          <div className="p-4 bg-gray-50 border-2 border-gray-200 rounded-lg">
            <p className="text-gray-700 text-center">
              We're sorry you can't make it! We'll miss you at our celebration.
            </p>
          </div>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          variant="primary"
          fullWidth
          disabled={isLoading}
          className="text-lg py-4"
        >
          {isLoading ? (
            <span className="flex items-center justify-center">
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Submitting...
            </span>
          ) : (
            'Submit RSVP'
          )}
        </Button>

        <p className="text-sm text-gray-600 text-center mt-4">
          Having trouble? Contact us at{' '}
          <a
            href="mailto:hello@nikiniandkeith.com"
            className="text-pink hover:text-pink-dark underline"
          >
            hello@nikiniandkeith.com
          </a>
        </p>
      </form>
    </div>
  );
}
